// Merlin's questions — the group's agenda for the next meeting.
//
// Workflow agreed at the 2026-09-08 check-in: questions are NOT answered live
// while Merlin runs; Merlin surfaces them after digging through emails, the
// database and the meetings and still finding gaps. The group opens this tab
// at some point in the next meeting and talks them through; that transcript
// flows back through the roadmap agent, which updates the initiatives and
// removes the answered questions. So the tab is built as a walk-through:
// grouped by the person Merlin is asking, one card per question, each with the
// proposed decide-by date PJ asked for ("let's revisit and decide by <date>"
// beats an open-ended item), the initiative it belongs to and its health.
//
// 2026-09-21 admin ask (kanban 816b31c3): each card can ALSO be answered in
// text or dismissed right here. A response is stored pending and handed to the
// next roadmap run (the kickoff's ANSWERS FROM THE ROADMAP PAGE block), which
// applies it and drops the question — so a responded card stays visible as
// "answered/dismissed, waiting for Merlin" with Undo until that run lands. The
// meeting flow is unchanged; this is a second way in.
//
// 2026-09-27 assessment (kanban 929581ec), before the Alpha / Delta / Hadi
// trial: (1) a card is filed under the person the question NAMES (the API's
// addressed_to), not the initiative's first person; (2) at 50+ questions the
// tab was a wall, so each person's list is collapsed to a count by default and
// a "Needs an answer today" block at the top carries the questions that map
// to the integrity gaps (no owner, no date, blocked without a blocker); (3) a
// decide-by date that passed after the report was written reads as "asked on
// <date>, for the next meeting", not as a failure.
//
// 2026-09-28 check-in (kanban 9eb5f005): each card carries the quoted words
// the question rests on, with who said them and an "Open source" link into
// the transcript, so nobody has to click through meetings by id to work out
// what "sampling JTM" meant. A question Merlin has not quoted yet says so and
// lists the initiative's cited sources as links instead.
//
// 2026-10-12 owner ask: a question clicked anywhere opens the question pop-up
// (QuestionModal), the same card as here (QuestionCard). On this tab the
// card's question text opens it, with prev / next through the list it sits in.

import { useState } from 'react';
import type {
  ClarifyingQuestion,
  ClarifyingQuestionsResponse,
  GroupQuestion,
  InitiativeState,
  IntegrityReport,
  QuestionResponse,
  RelevanceSummary,
} from '../../services/roadmapReports';
import {
  GAP_KIND_LABELS,
  needsAnswerNow,
  questionAddressee,
  questionAddressees,
  questionWithoutDecideBy,
  UNASSIGNED,
  type TeamColors,
} from './roadmapViewUtils';
import type { OpenSourceFn } from './EvidenceList';
import { QuestionResponseControls, type RespondFn, type WithdrawFn } from './QuestionResponseControls';
import { QuestionDigestButton } from './QuestionDigestDialog';
import { QuestionCard, type OpenQuestionFn } from './QuestionCard';
import { RelevanceControls, type MarkRelevanceFn } from './RelevanceControls';
import { RelevancePanel } from './RelevancePanel';
import { cardClass } from './questionCardUtils';
import { DecideByChip } from './DecideByChip';
import styles from './RoadmapV2.module.css';

export { DecideByChip };

interface Props {
  data: ClarifyingQuestionsResponse | null;
  teamColors: TeamColors;
  onSelect: (key: string) => void;
  /** The tracked initiatives, so each card can show the row's health chip. */
  initiatives?: InitiativeState[];
  /** The integrity audit; drives the "Needs an answer today" block. */
  integrity?: IntegrityReport | null;
  /** Answer / dismiss a question in the UI; omitted → the tab is read-only. */
  onRespond?: RespondFn;
  /** Undo a pending response. */
  onWithdraw?: WithdrawFn;
  /** Which asks this reader may answer (a portal reader: only the ones
   * addressed to them); omitted → every ask, when onRespond is given. A
   * group ask has no addressee and is never a portal reader's. */
  canRespondTo?: (q: ClarifyingQuestion) => boolean;
  /** Admin: show "Email these questions…" (the per-lead / per-person digest, kanban bafe8216). */
  canEmail?: boolean;
  /** The page's Person filter: only that person's block is listed (a shared
   * question still shows, under them). */
  onlyPerson?: string | null;
  /** Opens the source viewer at a quote (kanban 9eb5f005). */
  onOpenSource?: OpenSourceFn;
  /** Opens a question in the pop-up (the card's question text becomes a button). */
  onOpen?: OpenQuestionFn;
  /** Mark a question relevant / not relevant (kanban 3709db93); omitted → no buttons. */
  onMark?: MarkRelevanceFn;
  /** The relevance rate per run and team, shown above the list. */
  relevanceSummary?: RelevanceSummary | null;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function ClarificationsPanel({
  data,
  onSelect,
  initiatives = [],
  integrity = null,
  onRespond,
  onWithdraw,
  canRespondTo,
  canEmail = false,
  onlyPerson = null,
  onOpenSource,
  onOpen,
  onMark,
  relevanceSummary = null,
}: Props) {
  // Persons whose list is opened; collapsed by default (the wall).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [allOpen, setAllOpen] = useState(false);
  if (!data) return null;
  const total = data.items.length + data.group_questions.length;
  if (total === 0 && data.unplaced.length === 0) return null;
  const today = new Date().toISOString().slice(0, 10);
  const canRespond = Boolean(onRespond && onWithdraw);
  const responded = [...data.items, ...data.group_questions].filter((q) => q.response).length;
  const reportDate = data.report_date;

  const controls = (
    identity: { initiative_key: string | null; question: string },
    response: QuestionResponse | null | undefined,
    extra?: { placeholder?: string; answerLabel?: string },
  ) =>
    canRespond && onRespond && onWithdraw && !canRespondTo ? (
      <QuestionResponseControls
        identity={identity}
        response={response}
        onRespond={onRespond}
        onWithdraw={onWithdraw}
        {...extra}
      />
    ) : null;

  const sourcesByKey = new Map(initiatives.map((i) => [i.initiative_key, i.sources]));

  // `own`: the card sits in a person's own list, so the "Name / Name:" lead-in
  // is dropped. `siblings` is the list the card sits in, for the pop-up's prev / next.
  const card = (q: ClarifyingQuestion, key: string, siblings: ClarifyingQuestion[], lead?: string, own = false) => (
    <QuestionCard
      key={key}
      q={q}
      today={today}
      reportDate={reportDate}
      sources={sourcesByKey.get(q.initiative_key) ?? []}
      lead={lead}
      own={own}
      onSelect={onSelect}
      onOpenSource={onOpenSource}
      onRespond={canRespond && (!canRespondTo || canRespondTo(q)) ? onRespond : undefined}
      onWithdraw={canRespond && (!canRespondTo || canRespondTo(q)) ? onWithdraw : undefined}
      onOpen={onOpen}
      onMark={onMark}
      siblings={siblings}
    />
  );

  // Group by the person the question is addressed to, else Unassigned.
  const byPerson = new Map<string, ClarifyingQuestion[]>();
  // A question addressed to two people is listed under both, so each person's
  // row is their whole list (it used to sit under the first name only).
  for (const q of data.items) {
    for (const who of questionAddressees(q)) byPerson.set(who, [...(byPerson.get(who) ?? []), q]);
  }
  const liveCount = (who: string) => (byPerson.get(who) ?? []).filter((q) => !q.response).length;
  // People who still owe an answer first; the ones who answered everything last.
  const persons = [...byPerson.keys()].filter((who) => !onlyPerson || who === onlyPerson).sort((a, b) => {
    const doneA = liveCount(a) === 0;
    const doneB = liveCount(b) === 0;
    if (doneA !== doneB) return doneA ? 1 : -1;
    return a === UNASSIGNED ? 1 : b === UNASSIGNED ? -1 : a.localeCompare(b);
  });
  const liveTotal = total - responded;
  // Live questions only: an answered one no longer needs an answer first.
  const urgent = needsAnswerNow(data.items.filter((q) => !q.response), integrity?.gaps ?? []);

  const isOpen = (who: string) => allOpen || expanded.has(who);
  const togglePerson = (who: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(who)) next.delete(who);
      else next.add(who);
      return next;
    });

  return (
    <>
      {total > 0 && (
        <section className={`${styles.panel} ${styles.askPanel}`} aria-label="Merlin needs clarification">
          <div className={styles.askHead}>
            <div>
              <h2 className={styles.askTitle}>
                <span className={styles.badge}>{liveTotal}</span> open question{liveTotal === 1 ? '' : 's'} from Merlin
              </h2>
              <p className={styles.askHow} data-testid="ask-summary">
                {responded > 0 && <>{responded} answered, waiting for Merlin&apos;s next run · </>}
                {data.items.filter((q) => !q.response).length} for people, {data.group_questions.filter((q) => !q.response).length} for the group
                {reportDate && <> · asked {reportDate}</>}
              </p>
            </div>
            {canEmail && (
              <div className={styles.askEmail}>
                <QuestionDigestButton />
              </div>
            )}
          </div>

          {onMark && <RelevancePanel summary={relevanceSummary} />}

          {urgent.length > 0 && (
            <div className={`${styles.askPerson} ${styles.askUrgent}`} data-testid="ask-urgent">
              <div className={styles.askPersonHead}>
                <span className={`${styles.avatar} ${styles.avatarUrgent}`} aria-hidden="true">!</span>
                <h3>Needs an answer first</h3>
                <span className={styles.meta}>
                  {urgent.length} question{urgent.length === 1 ? '' : 's'} that would close a gap the audit flags: no owner, no date, or blocked with nobody named
                </span>
              </div>
              <ol className={styles.askCards}>
                {urgent.map(({ gapKind, question: q }) =>
                  card(q, `urgent:${q.initiative_key}:${q.question}`, urgent.map((u) => u.question), `${GAP_KIND_LABELS[gapKind] ?? gapKind} · ${questionAddressee(q)}`),
                )}
              </ol>
            </div>
          )}

          <div className={styles.askPersonsHead}>
            <span className={styles.meta}>by person · click a name to open their questions</span>
            <button type="button" className={styles.linkBtn} onClick={() => { setAllOpen((v) => !v); setExpanded(new Set()); }}>
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          </div>

          {persons.map((who) => {
            const qs = byPerson.get(who)!;
            const open = isOpen(who);
            const answered = qs.filter((q) => q.response).length;
            return (
              <div key={who} className={styles.askPerson} data-testid={`ask-${who}`}>
                <button
                  type="button"
                  className={`${styles.askPersonHead} ${styles.askPersonToggle}`}
                  onClick={() => togglePerson(who)}
                  aria-expanded={open}
                  aria-controls={`ask-list-${who}`}
                >
                  <span className={styles.avatar} aria-hidden="true">{who === UNASSIGNED ? '?' : initials(who)}</span>
                  <h3>{who}</h3>
                  <span className={styles.meta}>
                    {answered === qs.length
                      ? `all ${qs.length} answered, waiting for Merlin`
                      : `${qs.length - answered} to answer${answered > 0 ? ` · ${answered} answered` : ''}`}
                  </span>
                  <span className={styles.askChevron} aria-hidden="true">{open ? '▾' : '▸'}</span>
                </button>
                {open && (
                  <ol className={styles.askCards} id={`ask-list-${who}`}>
                    {(() => {
                      const ordered = [...qs.filter((q) => !q.response), ...qs.filter((q) => q.response)];
                      return ordered.map((q, idx) => card(q, `${who}:${q.initiative_key}:${idx}`, ordered, undefined, true));
                    })()}
                  </ol>
                )}
              </div>
            );
          })}

          {data.group_questions.length > 0 && (
            <div className={styles.askPerson} data-testid="ask-group">
              <div className={styles.askPersonHead}>
                <span className={`${styles.avatar} ${styles.avatarGroup}`} aria-hidden="true">all</span>
                <h3>For the group</h3>
                <span className={styles.meta}>not tied to one initiative</span>
              </div>
              <ol className={styles.askCards}>
                {data.group_questions.map((g: GroupQuestion) => (
                  <li key={g.question} className={cardClass(g.response)}>
                    <div className={styles.askQuestion}>{questionWithoutDecideBy(g.question)}</div>
                    <div className={styles.askCardFoot}>
                      <DecideByChip question={g.question} today={today} reportDate={reportDate} />
                    </div>
                    {onMark && (
                      <RelevanceControls identity={{ initiative_key: null, question: g.question }} relevance={g.relevance} onMark={onMark} />
                    )}
                    {controls({ initiative_key: null, question: g.question }, g.response)}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>
      )}
      {data.unplaced.length > 0 && (
        <section className={`${styles.panel} ${styles.unsurePanel}`} aria-label="Merlin isn't sure">
          <div className={styles.panelHead}>
            <h2>Merlin isn&apos;t sure — {data.unplaced.length} unplaced</h2>
            <span className={styles.meta}>
              heard, but not placed into an initiative — tell Merlin where these belong (or that they don&apos;t)
            </span>
          </div>
          <ul className={styles.unsureList}>
            {data.unplaced.map((u: GroupQuestion) => (
              <li key={u.question} className={u.response ? styles.unsureResponded : undefined}>
                <div>{u.question}</div>
                {controls(
                  { initiative_key: null, question: u.question },
                  u.response,
                  {
                    placeholder: 'Which initiative does this belong to — or what is it?',
                    answerLabel: 'Tell Merlin',
                  },
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
